/**
 * 定期便機能（定期便専用）
 * 定期便プランの選択と価格計算
 */

class SubscriptionPlanSelector {
  constructor() {
    this.variantSelects = document.querySelector('variant-selects');
    if (!this.variantSelects) return;

    this.productFormId = this.variantSelects.querySelector('input[name="id"]')?.form?.id;
    this.sellingPlanInputs = this.variantSelects.querySelectorAll('.subscription-plan-item__input');
    this.packOptionInputs = this.variantSelects.querySelectorAll('.pack-option__input');
    this.variantJson = null;
    this.sellingPlanJson = null;

    this.init();
  }

  init() {
    // JSONデータを読み込み
    this.loadJsonData();
    
    // 定期便プラン選択のイベントリスナー
    this.sellingPlanInputs.forEach(input => {
      input.addEventListener('change', this.handleSellingPlanChange.bind(this));
    });

    // パックオプション選択のイベントリスナー
    this.packOptionInputs.forEach(input => {
      input.addEventListener('change', this.handlePackOptionChange.bind(this));
    });

    // バリアント選択のイベントリスナー（button/swatchタイプ用）
    const variantInputs = this.variantSelects.querySelectorAll('input[type="radio"][name^="options"], input[type="radio"][name^="パック"]');
    variantInputs.forEach(input => {
      input.addEventListener('change', this.handleVariantChange.bind(this));
    });

    // 初期表示時の価格を計算
    this.updateAllPrices();
    
    // 初期表示時にバリアントメタフィールドから説明文を設定
    this.updatePlanDescriptionFromVariant();
    
    // 初期選択されているプランをフォームに設定
    const selectedPlanInput = this.variantSelects.querySelector('.subscription-plan-item__input:checked');
    if (selectedPlanInput) {
      this.updateFormSellingPlan(selectedPlanInput.value);
    }
  }

  loadJsonData() {
    // バリアントJSONを読み込み
    const variantJsonElement = this.variantSelects.querySelector('[data-variant-json]');
    if (variantJsonElement) {
      try {
        this.variantJson = JSON.parse(variantJsonElement.textContent);
      } catch (e) {
        console.error('バリアントJSONの読み込みに失敗しました:', e);
      }
    }

    // 定期便プランJSONを読み込み
    const sellingPlanJsonElement = this.variantSelects.querySelector('[data-selling-plan-json]');
    if (sellingPlanJsonElement) {
      try {
        this.sellingPlanJson = JSON.parse(sellingPlanJsonElement.textContent);
      } catch (e) {
        console.error('定期便プランJSONの読み込みに失敗しました:', e);
      }
    }
  }

  handleSellingPlanChange(event) {
    const selectedPlanId = event.target.value;
    
    // フォームに定期便プランIDを設定
    this.updateFormSellingPlan(selectedPlanId);
    
    // 価格を更新
    this.updateAllPrices();
  }

  handlePackOptionChange(event) {
    // パック数が変更された時も価格を再計算
    this.updateAllPrices();
    
    // バリアントメタフィールドから説明文を更新
    this.updatePlanDescriptionFromVariant();
  }

  handleVariantChange(event) {
    // バリアントが変更された時も価格を再計算
    this.updateAllPrices();
    
    // バリアントメタフィールドから説明文を更新
    this.updatePlanDescriptionFromVariant();
  }

  updatePlanDescriptionFromVariant() {
    // 選択されたバリアントオプションを取得（pack-option__input または button/swatch タイプの input）
    let selectedVariantInput = this.variantSelects.querySelector('.pack-option__input:checked');
    
    // pack-option__input がない場合は、button/swatch タイプの input を探す
    if (!selectedVariantInput) {
      const variantInputs = this.variantSelects.querySelectorAll('input[type="radio"][name^="options"], input[type="radio"][name^="パック"]');
      selectedVariantInput = Array.from(variantInputs).find(input => input.checked && input.dataset.subscDesc);
    }

    if (!selectedVariantInput) return;

    // バリアントメタフィールドから説明文を取得
    const subscDesc = selectedVariantInput.dataset.subscDesc;

    // 説明文を更新
    const descriptionElements = this.variantSelects.querySelectorAll('.subscription-plan-item__description');
    descriptionElements.forEach(element => {
      if (subscDesc) {
        element.textContent = subscDesc;
        element.style.display = 'block';
      } else {
        // メタフィールドがない場合は元の説明文を表示（plan.description）
        element.style.display = 'block';
      }
    });
  }

  updateAllPrices() {
    // 選択されたバリアントオプションを取得（pack-option__input または button/swatch タイプの input）
    let selectedVariantInput = this.variantSelects.querySelector('.pack-option__input:checked');
    
    // pack-option__input がない場合は、button/swatch タイプの input を探す
    if (!selectedVariantInput) {
      const variantInputs = this.variantSelects.querySelectorAll('input[type="radio"][name^="options"], input[type="radio"][name^="パック"]');
      selectedVariantInput = Array.from(variantInputs).find(input => input.checked && input.dataset.variantId);
    }

    if (!selectedVariantInput) return;

    const selectedVariantId = selectedVariantInput.dataset.variantId;
    const selectedVariant = this.variantJson?.find(v => v.id.toString() === selectedVariantId?.toString());
    
    if (!selectedVariant) return;

    const basePrice = selectedVariant.price;

    // 全プランの価格を更新
    this.sellingPlanInputs.forEach(input => {
      const planId = input.value;
      const plan = this.getSellingPlanById(planId);
      
      if (plan) {
        const discountedPrice = this.calculateDiscountedPrice(basePrice, plan);
        const priceElement = this.variantSelects.querySelector(`.subscription-plan-item__price-value[data-plan-id="${planId}"]`);
        
        if (priceElement) {
          priceElement.textContent = this.formatPrice(discountedPrice);
        }
      }
    });
  }

  calculateDiscountedPrice(basePrice, plan) {
    if (!plan || !plan.price_adjustments || plan.price_adjustments.length === 0) {
      return basePrice;
    }

    const adjustment = plan.price_adjustments[0];
    let discountedPrice = basePrice;

    if (adjustment.value_type === 'percentage') {
      const discountAmount = basePrice * (adjustment.value / 100);
      discountedPrice = basePrice - discountAmount;
    } else if (adjustment.value_type === 'fixed_amount') {
      discountedPrice = basePrice - adjustment.value;
    } else if (adjustment.value_type === 'price') {
      discountedPrice = adjustment.value;
    }

    return Math.max(0, Math.round(discountedPrice));
  }

  getSellingPlanById(planId) {
    if (!this.sellingPlanJson || !planId) return null;

    for (const group of this.sellingPlanJson.selling_plan_groups) {
      const plan = group.selling_plans.find(p => p.id.toString() === planId.toString());
      if (plan) return plan;
    }
    return null;
  }

  updateFormSellingPlan(planId) {
    // hidden inputで定期便プランIDをフォームに追加/更新
    const form = document.getElementById(this.productFormId);
    if (!form) return;

    let sellingPlanInput = form.querySelector('input[name="selling_plan"]');
    
    if (!sellingPlanInput) {
      sellingPlanInput = document.createElement('input');
      sellingPlanInput.type = 'hidden';
      sellingPlanInput.name = 'selling_plan';
      form.appendChild(sellingPlanInput);
    }
    
    sellingPlanInput.value = planId || '';
  }

  formatPrice(cents) {
    const yen = Math.round(cents / 100);
    return `¥${yen.toLocaleString('ja-JP')}`;
  }
}

// DOMContentLoaded時に初期化
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new SubscriptionPlanSelector();
  });
} else {
  new SubscriptionPlanSelector();
}

// Shopifyセクションレンダリング時の再初期化対応
document.addEventListener('shopify:section:load', () => {
  new SubscriptionPlanSelector();
});